import type { MatchState } from '../match/MatchState';
import type { PlayerRuntimeState } from '../match/PlayerRuntimeState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { clamp, distance, lerp, type Vector2 } from '../match/Vector2';
import { ANCHOR_PROFILES } from './AnchorPolicy';
import type { FormationRelationship } from './FormationRelationships';
import type { PlayerIntentProposal } from './PlayerIntent';
import { roleFamily, roleProfile } from './RoleProfiles';
import type { TeamAIState } from './TeamAIDirector';
import { TacticalRoleSystem } from './TacticalRoleSystem';

const relativeProgress = (player: PlayerRuntimeState, x: number) => PITCH_GEOMETRY.relativeProgress(player.teamSide, x);
const worldX = (side: 0 | 1, progress: number) => PITCH_GEOMETRY.worldX(side, progress);

export class DefensiveCoordinator {
  private readonly tacticalRoles = new TacticalRoleSystem();

  createIntents(
    state: MatchState,
    owner: PlayerRuntimeState,
    plan: TeamAIState,
    relationships: readonly FormationRelationship[],
  ): Map<string, PlayerIntentProposal> {
    const intents = new Map<string, PlayerIntentProposal>();
    const defenders = state.players.filter((player) => player.teamSide !== owner.teamSide
      && player.positionType !== 'GK' && !this.isReturningToShape(player, state));
    const pressers = [...defenders]
      .sort((a, b) => this.pressPriority(a, owner) - this.pressPriority(b, owner) || a.playerId.localeCompare(b.playerId))
      .slice(0, plan.presserCount);
    if (!pressers.length) return intents;

    pressers.forEach((presser, index) => {
      const pressProgress = relativeProgress(presser, owner.position.x);
      const goalSideGap = 2.7 - plan.pressing * 2;
      intents.set(presser.playerId, {
        movementState: 'PRESSING',
        reason: 'PRESS_BALL',
        idealTarget: {
          x: worldX(presser.teamSide, clamp(pressProgress - goalSideGap - index * 0.8, 4, 94)),
          y: clamp(lerp(owner.position.y, 50, 0.08) + (index === 0 ? 0 : owner.position.y < 50 ? 3 : -3), 5, 95),
        },
        durationSeconds: state.config.intentMaximumDurationSeconds,
        markingTargetId: owner.playerId,
      });
    });

    const primaryPresser = pressers[0];
    const presserIds = new Set(pressers.map((player) => player.playerId));
    const coverDistance = state.config.defensiveCoverDistance * (1.2 - plan.defensiveLine * 0.35);
    const coverTarget = this.coverTarget(primaryPresser, owner, coverDistance);
    const coverCandidates = defenders
      .filter((player) => !presserIds.has(player.playerId) && (player.positionType === 'DF' || player.positionType === 'MF'));
    // The screen in front of the back line belongs to midfield whenever a
    // midfielder is available. Centre-backs still cover as a fallback, but
    // this prevents every recovery from leaving midfield watching play ahead.
    const midfieldScreen = coverCandidates
      .filter((player) => player.positionType === 'MF')
      .sort((a, b) => this.midfieldScreenPriority(a, coverTarget) - this.midfieldScreenPriority(b, coverTarget)
        || a.playerId.localeCompare(b.playerId))[0];
    const cover = (midfieldScreen ? [midfieldScreen] : coverCandidates)
      .sort((a, b) => this.coverPriority(a, primaryPresser, relationships) - this.coverPriority(b, primaryPresser, relationships)
        || distance(a.position, coverTarget) - distance(b.position, coverTarget)
        || a.playerId.localeCompare(b.playerId))[0];
    if (cover) intents.set(cover.playerId, {
      movementState: 'MARKING', reason: 'COVER_GOAL', idealTarget: coverTarget,
      durationSeconds: state.config.intentMaximumDurationSeconds, markingTargetId: owner.playerId,
    });

    const availableMarkers = defenders.filter((player) => !presserIds.has(player.playerId)
      && player.playerId !== cover?.playerId && (player.positionType === 'DF' || player.positionType === 'MF'));
    const threats = state.players
      .filter((player) => player.teamSide === owner.teamSide && player.playerId !== owner.playerId && player.positionType !== 'GK')
      .sort((a, b) => this.threatPriority(b) - this.threatPriority(a) || a.playerId.localeCompare(b.playerId));
    let assignedMarkers = 0;
    for (const threat of threats) {
      if (!availableMarkers.length || assignedMarkers >= plan.markingLimit) break;
      const markingDistance = state.config.markingDistance * (1.15 - plan.pressing * 0.3);
      const target = this.markingTarget(availableMarkers[0].teamSide, threat, markingDistance);
      availableMarkers.sort((a, b) => this.markerSuitability(a, threat, target) - this.markerSuitability(b, threat, target)
        || a.playerId.localeCompare(b.playerId));
      const marker = availableMarkers.shift()!;
      intents.set(marker.playerId, {
        movementState: 'MARKING', reason: 'MARK_THREAT', idealTarget: target,
        durationSeconds: state.config.intentMaximumDurationSeconds, markingTargetId: threat.playerId,
      });
      assignedMarkers++;
    }

    return intents;
  }

  private pressPriority(player: PlayerRuntimeState, owner: PlayerRuntimeState): number {
    const family = roleFamily(player.role, player.positionType);
    const lanePenalty = family === 'FULL_BACK' || family === 'WING_BACK'
      ? Math.abs(player.homePosition.y - owner.position.y) * 0.18 : 0;
    return distance(player.position, owner.position) + lanePenalty
      - roleProfile(player.role, player.positionType).pressPriority * 0.025
      - (this.tacticalRoles.movementBias(player.tacticalProfile.assignedRole, 'pressBias') - 1) * 18;
  }

  private markerSuitability(player: PlayerRuntimeState, threat: PlayerRuntimeState, target: Vector2): number {
    const family = roleFamily(player.role, player.positionType);
    const wideDefender = family === 'FULL_BACK' || family === 'WING_BACK';
    const wideThreat = threat.position.y <= 30 || threat.position.y >= 70;
    const sameLane = (player.homePosition.y < 50) === (threat.position.y < 50);
    const laneAdjustment = wideDefender
      ? wideThreat && sameLane ? -18 : wideThreat ? 14 : 8
      : wideThreat ? 7 : 0;
    return distance(player.position, target) + laneAdjustment;
  }

  private coverPriority(
    player: PlayerRuntimeState,
    presser: PlayerRuntimeState,
    relationships: readonly FormationRelationship[],
  ): number {
    const relationshipBonus = relationships.some((item) => item.type === 'COVER'
      && item.fromPlayerId === player.playerId && item.toPlayerId === presser.playerId) ? 20 : 0;
    return -roleProfile(player.role, player.positionType).coverPriority - relationshipBonus
      - (this.tacticalRoles.movementBias(player.tacticalProfile.assignedRole, 'recoveryBias') - 1) * 25;
  }

  private midfieldScreenPriority(player: PlayerRuntimeState, target: Vector2): number {
    const profile = roleProfile(player.role, player.positionType);
    const family = roleFamily(player.role, player.positionType);
    const holdingBonus = family === 'HOLDING_MIDFIELDER' ? 16 : family === 'CENTRAL_MIDFIELDER' ? 8 : 0;
    return distance(player.position, target) - profile.coverPriority * .32 - holdingBonus
      - (this.tacticalRoles.movementBias(player.tacticalProfile.assignedRole, 'recoveryBias') - 1) * 22;
  }

  private threatPriority(player: PlayerRuntimeState): number {
    return relativeProgress(player, player.position.x) + roleProfile(player.role, player.positionType).runPriority
      + player.stats.kick * 0.15 + player.stats.speed * 0.1;
  }

  private coverTarget(defender: PlayerRuntimeState, owner: PlayerRuntimeState, coverDistance: number): Vector2 {
    const ownerProgressForDefender = relativeProgress(defender, owner.position.x);
    return {
      x: worldX(defender.teamSide, clamp(ownerProgressForDefender - coverDistance, 5, 88)),
      y: lerp(owner.position.y, 50, 0.35),
    };
  }

  private markingTarget(defendingSide: 0 | 1, threat: PlayerRuntimeState, markingDistance: number): Vector2 {
    const threatProgressForDefender = PITCH_GEOMETRY.relativeProgress(defendingSide, threat.position.x);
    return {
      x: worldX(defendingSide, clamp(threatProgressForDefender - markingDistance, 4, 94)),
      y: threat.position.y,
    };
  }

  private isReturningToShape(player: PlayerRuntimeState, state: MatchState): boolean {
    if (player.intent.reason !== 'RETURN_TO_SHAPE') return false;
    const minimumReturnElapsed = player.intent.expiresAt === undefined || state.time >= player.intent.expiresAt;
    const reachedAnchor = distance(player.position, player.anchorPosition) <= ANCHOR_PROFILES[player.positionType].returnRadius;
    return !minimumReturnElapsed || !reachedAnchor;
  }
}
